import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Add New Course — step 1: pick a tee-sheet provider.
 *
 * Each tile shows the provider's status + how many courses + resellers it
 * already serves. Click a tile to drill into that provider's detail page,
 * where the actual course-add + rate-set UI lives.
 */
export default function CourseIntegrationAdd() {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true); setError(null)
    tournamentApi.get('/admin/course-integrations/providers')
      .then(r => setProviders(r.data.providers || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AdminShell title="Add New Course">
      <div style={{ marginBottom: 16 }}>
        <Link to="/admin/course-integrations" style={linkStyle}>← Back to courses</Link>
      </div>

      <p style={{ color: '#64748b', margin: '0 0 24px', maxWidth: 720, lineHeight: 1.5 }}>
        Pick the tee-sheet provider that powers this course. You'll set the master credentials
        (if needed) and the default rate code on the next screen.
      </p>

      {error && <div style={errorBoxStyle}>{error}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {providers.map(p => (
            <Link key={p.key} to={`/admin/course-integrations/${p.key}`} style={cardLink}>
              <div style={{ ...cardStyle, ...(p.status === 'active' ? cardActive : null) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, color: '#0f172a' }}>{p.label}</div>
                  <StatusBadge status={p.status} />
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16, minHeight: 36, lineHeight: 1.4 }}>
                  {p.description}
                </div>
                <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                  <Stat label="Courses available" value={p.coursesAvailable} />
                  <Stat label="With rate set" value={p.coursesWithRate} highlight={p.coursesWithRate > 0} />
                  <Stat label="Resellers" value={p.resellers} highlight={p.resellers > 0} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </AdminShell>
  )
}

function StatusBadge({ status }) {
  const map = {
    active:     { bg: '#dcfce7', fg: '#166534', label: 'Active' },
    awaiting:   { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting setup' },
    not_setup:  { bg: '#fee2e2', fg: '#991b1b', label: 'Not configured' },
    per_course: { bg: '#e0e7ff', fg: '#3730a3', label: 'Per-course' },
    platform:   { bg: '#f1f5f9', fg: '#475569', label: 'Platform-owned' },
  }
  const m = map[status] || map.not_setup
  return <span style={{ ...pill(m.bg, m.fg), whiteSpace: 'nowrap' }}>{m.label}</span>
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? '#00d19f' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  )
}

const cardLink = { textDecoration: 'none', color: 'inherit' }
const cardStyle = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
  padding: 20, transition: 'transform .12s, box-shadow .12s', cursor: 'pointer',
}
const cardActive = { borderColor: '#00d19f33', boxShadow: '0 0 0 1px #00d19f22' }
const errorBoxStyle = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 6, marginBottom: 16, fontSize: 14 }
const linkStyle = { color: '#00d19f', textDecoration: 'none', fontSize: 14 }
function pill(bg, fg) {
  return { display: 'inline-block', padding: '2px 8px', background: bg, color: fg, borderRadius: 12, fontSize: 11, fontWeight: 600 }
}
